import React, { useState } from 'react';
import { useWallet } from '../context/WalletProvider';
import { useToast, readableError } from '../components/Toast';

// ---- small inline icon helper ----
const I = ({ d, cls = 'ic' }) => (
  <svg className={cls} viewBox="0 0 24 24"><path d={d} strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const TICKS = [
  'Professional Market Analysis',
  'Advanced MT5 Infrastructure',
  'Strategic Risk Management',
  'Transparent Performance Monitoring',
];

const TRUST = [
  { t: 'Registered Company', d: 'M9 12l2 2 4-4M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7l8-4z' },
  { t: 'Real MT5 Trading', d: 'M3 3v18h18M7 14l4-4 3 3 5-6' },
  { t: 'On-Chain USDT', d: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6' },
];

const STEPS = [
  { h: 'Register Your Account', p: 'Connect your wallet and link your MT5 account number to join the Zen Capital network.' },
  { h: 'Experts Trade The Markets', p: 'Our professional traders operate across Forex, commodities and indices on the MT5 platform.' },
  { h: 'Weekly Profit Is Recorded', p: 'Closed trading profit is reviewed and recorded every week for the network.' },
  { h: 'Earn & Claim In USDT', p: 'Your referral level income is credited in USDT — claim it to your wallet anytime.' },
];

const WHY = [
  { h: 'Advanced Technology', p: 'Powered by the industry-leading MT5 platform for seamless market access.', d: 'M4 17l6-6-6-6M12 19h8' },
  { h: 'Professional Expertise', p: 'Experienced analysts and traders monitoring market opportunities.', d: 'M12 14a4 4 0 100-8 4 4 0 000 8zM6 21v-1a6 6 0 0112 0v1' },
  { h: 'Risk-Focused', p: 'Disciplined strategies designed to manage market volatility effectively.', d: 'M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7l8-4z' },
  { h: 'Full Transparency', p: 'Clear reporting and performance tracking for informed decisions.', d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 100-6 3 3 0 000 6z' },
  { h: 'Global Markets', p: 'Trade across Forex, commodities, indices and other instruments.', d: 'M12 22a10 10 0 100-20 10 10 0 000 20zM2 12h20M12 2a15 15 0 010 20 15 15 0 010-20z' },
  { h: 'Continuous Innovation', p: 'Modern technology and data-driven insights optimise operations.', d: 'M9 18h6M10 22h4M12 2a7 7 0 00-4 12.7c.6.5 1 1.3 1 2.1h6c0-.8.4-1.6 1-2.1A7 7 0 0012 2z' },
];

const SERVICES = [
  'Forex Trading', 'Commodity Trading', 'Index Trading', 'Market Research',
  'Portfolio Management', 'Risk Management', 'MT5 Infrastructure',
];

const LEVELS = [
  { n: 'L1', pct: '10%', crit: 'No criteria' },
  { n: 'L2', pct: '5%', crit: '3 directs · 3 members in level 2' },
  { n: 'L3', pct: '4%', crit: '5 directs · 5 members in level 3' },
  { n: 'L4', pct: '3%', crit: '7 directs · 5 members in level 4' },
  { n: 'L5', pct: '3%', crit: '9 directs · 7 members in level 5' },
];

const FAQS = [
  { q: 'What is Zen Capital Technologies?', a: 'A technology-driven trading company that navigates global financial markets using disciplined strategies, market research and advanced trading tools on the MetaTrader 5 platform.' },
  { q: 'How does the level income work?', a: 'As your referral network participates, a share of the network\'s weekly trading profit is distributed to you across up to 5 levels, based on your team size. Income is credited in USDT.' },
  { q: 'When can I withdraw my income?', a: 'Your level income accumulates as a claimable USDT balance. You can claim it to your connected wallet at any time from your dashboard.' },
  { q: 'What is USDT and BEP-20?', a: 'USDT is a US-dollar stablecoin. Zen Capital pays income in USDT on the BNB Smart Chain (BEP-20), so transfers are fast and low-cost.' },
  { q: 'Do I need my own MT5 account?', a: 'Yes — you link your MT5 account number during registration so your trading activity can be matched to the network.' },
  { q: 'Is my personal data on the blockchain?', a: 'No. Your name, email and mobile are stored securely off-chain. Only your account number and wallet address are recorded on-chain.' },
];

export default function Landing() {
  const { connect, connecting } = useWallet();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const onConnect = async () => {
    try { setBusy(true); await connect(); toast.success('Wallet connected'); }
    catch (e) { toast.error(readableError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="landing">
      {/* HERO */}
      <section className="lp-hero">
        <div className="logo-orbit">
          <div className="ring-static a" />
          <div className="ring-static b" />
          <img src="/logo.png" alt="Zen Capital" />
        </div>
        <h1>Zen Capital <span className="grad">Technologies</span></h1>
        <p className="sub">Professional trading solutions powered by expertise &amp; technology. Unlock opportunities in global financial markets through advanced strategies, risk management and cutting-edge technology.</p>
        <div className="tagline">Trade Smarter · Grow Stronger</div>

        <div className="lp-cta-row">
          <button className="btn" onClick={onConnect} disabled={busy || connecting}>
            {busy || connecting ? <span className="btn-spin" /> : 'Connect Wallet'}
          </button>
          <a href="#how" className="btn ghost" style={{ textDecoration: 'none', display: 'block' }}>Learn More</a>
        </div>

        <div className="lp-ticks">
          {TICKS.map((t) => (
            <div className="lp-tick" key={t}>
              <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span>{t}</span>
            </div>
          ))}
        </div>

        <div className="trust-strip">
          {TRUST.map((c) => (
            <div className="trust-chip" key={c.t}>
              <I d={c.d} />
              <div className="t">{c.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="lp-section">
        <div className="lp-eyebrow">About</div>
        <h2 className="lp-h2">Who We Are</h2>
        <p className="lp-lead">
          Zen Capital Technologies is a technology-driven trading company focused on navigating global
          financial markets through disciplined strategies, market research and advanced trading tools.
        </p>
        <p className="lp-lead">
          We combine financial expertise with innovative technology to identify market opportunities
          while maintaining a strong focus on risk management and capital preservation. Using the
          MetaTrader 5 platform, we provide a professional environment built for efficiency,
          transparency and performance tracking.
        </p>
      </section>

      {/* HOW IT WORKS */}
      <section className="lp-section" id="how">
        <div className="lp-eyebrow">Process</div>
        <h2 className="lp-h2">How It Works</h2>
        {STEPS.map((s, i) => (
          <div className="step" key={s.h}>
            <div className="num">{i + 1}</div>
            <div className="body"><h4>{s.h}</h4><p>{s.p}</p></div>
          </div>
        ))}
      </section>

      {/* LEVEL BREAKDOWN */}
      <section className="lp-section">
        <div className="lp-eyebrow">Rewards</div>
        <h2 className="lp-h2">5-Level Income</h2>
        <p className="lp-lead">Earn a share of your network's weekly trading profit across five referral levels, paid in USDT.</p>
        <div className="lvl-card">
          {LEVELS.map((l) => (
            <div className="lvl-line" key={l.n}>
              <span className="lp-badge">{l.n}</span>
              <span className="lp-pct">{l.pct}</span>
              <span className="lp-crit">{l.crit}</span>
            </div>
          ))}
          <div className="lvl-total">TOTAL DISTRIBUTION · 25%</div>
        </div>
      </section>

      {/* WHY ZEN */}
      <section className="lp-section">
        <div className="lp-eyebrow">Advantages</div>
        <h2 className="lp-h2">Why Choose Zen Capital</h2>
        <div className="why-grid">
          {WHY.map((w) => (
            <div className="why-card" key={w.h}>
              <I d={w.d} />
              <h4>{w.h}</h4><p>{w.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="lp-section">
        <div className="lp-eyebrow">Services</div>
        <h2 className="lp-h2">What We Offer</h2>
        <div className="svc-wrap">
          {SERVICES.map((s) => <span className="svc" key={s}>{s}</span>)}
        </div>
      </section>

      {/* FAQ */}
      <section className="lp-section">
        <div className="lp-eyebrow">Questions</div>
        <h2 className="lp-h2">FAQ</h2>
        {FAQS.map((f, i) => (
          <div className="faq-item" key={i}>
            <button className={'faq-q' + (openFaq === i ? ' open' : '')} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
              <span>{f.q}</span>
              <svg className="chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            <div className={'faq-a' + (openFaq === i ? ' open' : '')}><p>{f.a}</p></div>
          </div>
        ))}
      </section>

      {/* FINAL CTA */}
      <section className="final-cta">
        <h3>Ready to Explore Market Opportunities?</h3>
        <p>Join Zen Capital Technologies and discover a smarter approach to financial market participation through technology, strategy and professional expertise.</p>
        <div style={{ maxWidth: 280, margin: '0 auto' }}>
          <button className="btn gold" onClick={onConnect} disabled={busy || connecting}>
            {busy || connecting ? <span className="btn-spin" /> : 'Start Your Journey'}
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="f-brand">ZEN CAPITAL</div>
        <div className="f-tag">Smart Trading · Shared Growth</div>

        <div className="lp-socials">
          {/* TODO: replace # with real links */}
          <a className="lp-social" href="#" aria-label="Telegram"><svg viewBox="0 0 24 24"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" /></svg></a>
          <a className="lp-social" href="#" aria-label="Instagram"><svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" /></svg></a>
          <a className="lp-social" href="#" aria-label="WhatsApp"><svg viewBox="0 0 24 24"><path d="M21 11.5a8.5 8.5 0 01-12.6 7.4L3 21l2.2-5.3A8.5 8.5 0 1121 11.5z" strokeLinecap="round" strokeLinejoin="round" /></svg></a>
          <a className="lp-social" href="#" aria-label="Website"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 010 18 15 15 0 010-18z" /></svg></a>
        </div>

        <div className="lp-contact">
          Support: <a href="mailto:support@zencapital.com">support@zencapital.com</a>
        </div>

        <p className="lp-disclaimer">
          Trading in financial markets involves risk. Zen Capital Technologies provides trading and
          technology services; past performance does not guarantee future results. Please participate
          responsibly. This platform distributes referral-based income in USDT and is not a deposit or
          guaranteed-return product.
        </p>
        <div className="lp-copy">© {new Date().getFullYear()} Zen Capital Technologies. All rights reserved.</div>
      </footer>
    </div>
  );
}
